import { useState, useEffect } from "react";
import { Users, Calendar, DollarSign, MessageSquare, TrendingUp, TrendingDown, Clock, ArrowRight, Package } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { useAuth } from "@/context/AuthContext";

const EXPENSE_COLORS = ["#2E7D32", "#C8922A", "#1B2A5E", "#EAB308", "#F97316"];

export default function Dashboard() {
  const { api, user, employee } = useAuth();
  const [stats, setStats] = useState(null);
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    api().get("/dashboard/stats").then(r => setStats(r.data)).catch(() => {});
    return () => clearInterval(timer);
  }, [api]);

  if (!stats) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-navy/20 border-t-navy rounded-full animate-spin" /></div>;

  const statCards = [
    { title: "Total Employees", value: stats.total_employees, icon: Users, color: "bg-blue-500", trend: "Active workforce", up: true },
    { title: "Present Today", value: stats.present_today, icon: Calendar, color: "bg-green-500", trend: `${stats.total_employees > 0 ? Math.round((stats.present_today / stats.total_employees) * 100) : 0}% attendance`, up: true },
    { title: "Monthly Revenue", value: `${(stats.monthly_revenue / 100000).toFixed(1)}L`, icon: DollarSign, color: "bg-amber-500", trend: "+12% vs last month", up: true },
    { title: "Pending Inquiries", value: stats.pending_inquiries, icon: MessageSquare, color: "bg-red-500", trend: "Needs attention", up: false },
  ];

  const expenseData = [
    { name: "Raw Materials", value: 35, color: "#2E7D32" },
    { name: "Labor", value: 30, color: "#C8922A" },
    { name: "Utilities", value: 15, color: "#1B2A5E" },
    { name: "Transport", value: 12, color: "#EAB308" },
    { name: "Packaging", value: 8, color: "#F97316" },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-serif text-navy tracking-tight">Welcome back, <span className="text-forest">{employee?.name?.split(" ")[0] || user?.username}!</span></h2>
          <p className="text-slate-400 flex items-center gap-2 mt-1 font-sans text-sm">
            <Clock className="w-4 h-4" />
            {time.toLocaleDateString("en-IN", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
            {" | "}{time.toLocaleTimeString("en-IN")}
          </p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, i) => (
          <div key={i} className="card-dashboard" data-testid={`stat-${stat.title.toLowerCase().replace(/ /g, "-")}`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-slate-400 text-sm font-sans">{stat.title}</p>
                <p className="text-3xl font-bold text-navy font-mono mt-1">{stat.value}</p>
              </div>
              <div className={`w-11 h-11 ${stat.color} rounded-lg flex items-center justify-center`}>
                <stat.icon className="w-5 h-5 text-white" />
              </div>
            </div>
            <div className="flex items-center gap-1 mt-3">
              {stat.up ? <TrendingUp className="w-4 h-4 text-green-500" /> : <TrendingDown className="w-4 h-4 text-red-500" />}
              <span className={`text-xs font-sans ${stat.up ? "text-green-600" : "text-red-600"}`}>{stat.trend}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="card-dashboard">
          <h3 className="text-lg font-serif text-navy mb-4">Revenue vs Expenses</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={stats.revenue_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" stroke="#94A3B8" tick={{ fontSize: 12, fontFamily: "JetBrains Mono" }} />
                <YAxis stroke="#94A3B8" tick={{ fontSize: 12, fontFamily: "JetBrains Mono" }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                <Tooltip contentStyle={{ border: "1px solid #E2E8F0", borderRadius: "8px", fontFamily: "Public Sans" }} formatter={v => [`Rs.${v.toLocaleString()}`, ""]} />
                <Line type="monotone" dataKey="income" stroke="#2E7D32" strokeWidth={2} name="Revenue" dot={false} />
                <Line type="monotone" dataKey="expenses" stroke="#C8922A" strokeWidth={2} name="Expenses" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-dashboard">
          <h3 className="text-lg font-serif text-navy mb-4">Expense Breakdown</h3>
          <div className="h-64 flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={expenseData} cx="50%" cy="50%" innerRadius={60} outerRadius={85} paddingAngle={3} dataKey="value">
                  {expenseData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip formatter={(v) => [`${v}%`, ""]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap justify-center gap-3 mt-2">
            {expenseData.map((item, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                <span className="text-xs text-slate-500 font-sans">{item.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card-dashboard">
        <h3 className="text-lg font-serif text-navy mb-4">Today's Production</h3>
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="text-center p-4 bg-green-50 rounded-lg border border-green-100">
            <p className="text-3xl font-bold text-forest font-mono">{(stats.monthly_production_kg / 1000).toFixed(1)}</p>
            <p className="text-slate-600 text-sm font-sans mt-1">Tons This Month</p>
          </div>
          <div className="text-center p-4 bg-amber-50 rounded-lg border border-amber-100">
            <p className="text-3xl font-bold text-coir font-mono">Rs.{(stats.monthly_revenue / 100000).toFixed(1)}L</p>
            <p className="text-slate-600 text-sm font-sans mt-1">Monthly Revenue</p>
          </div>
          <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-100">
            <p className="text-3xl font-bold text-navy font-mono">Rs.{((stats.monthly_revenue - stats.monthly_expenses) / 100000).toFixed(1)}L</p>
            <p className="text-slate-600 text-sm font-sans mt-1">Monthly Profit</p>
          </div>
        </div>
      </div>
    </div>
  );
}
